import React, {useState} from 'react'
import Icon from '../../core/shared/Icon'
import {CartItem, computeCartItemPrice} from './cart-item'
import {formatPrice} from '../../utils/currency-format'
import {
  SubAccordion,
  SectionBlock,
  SubAccordionHeader,
  SubAccordionMeta,
  SubAccordionTotal,
  ChevronIcon,
  LocationAccordion,
  LocationAccordionWrapper,
  LocationHeader,
  LocationMeta,
} from '../../features/OrderSummary/OrderSummary.styles'
import {groupItemsByLocation, getLocationLabel} from './utils'

function categorizeItems(items) {
  const software = []
  const addons = []
  items.forEach(item => {
    // All plans from the API share prodType: 'plan'. True software centers
    // (CC, MC, WC, RC, Keap Max) have no groupTitle; add-on-like plans
    // (SEO, TL, Strategic Content, Call Tracking) have a groupTitle set by
    // the catalog, so we use that to route them to the correct section.
    if (item.prodType === 'plan' && !item.groupTitle) software.push(item)
    else addons.push(item)
  })
  return {software, addons}
}

function getGroupTotal(items, country) {
  const total = items.reduce((sum, item) => {
    const itemTotal = computeCartItemPrice(item, {country})
    const currencyCode = (item.nextIntervalCurrency || item.currency)?.currency
    const addonTotal = (item.addons || [])
      .filter(a => a.prodType === 'itemBasedAddon')
      .reduce((addonSum, addon) => {
        const addonCurrency = addon.currencies?.find(
          c => c.currency === currencyCode,
        )
        const addonPrice =
          (addonCurrency?.unitAmount || 0) * (addon.quantity || 1)
        const isSeobaseAddon = [
          'addon-5-seopb-m2m',
          'addon-5-seokey-m2m',
          'addon-4-seopb-m2m',
          'addon-4-seokey-m2m',
        ].includes(addon.code)
        return (
          addonSum +
          (currencyCode === 'AUD' && isSeobaseAddon
            ? addonPrice * 1.1
            : addonPrice)
        )
      }, 0)
    return sum + itemTotal + addonTotal
  }, 0)
  return formatPrice(total, 1, '', country)
}

function CategoryAccordion({label, items, country, open, onToggle}) {
  if (!items.length) return null
  return (
    <SubAccordion style={{marginBottom: '8px'}}>
      <SubAccordionHeader onClick={onToggle}>
        <SubAccordionMeta>
          <ChevronIcon open={open}>
            <Icon name="downArrow" />
          </ChevronIcon>
          <div className="sub-category">
            {label} <span>- {items.length} items</span>
          </div>
        </SubAccordionMeta>
        <SubAccordionTotal>{getGroupTotal(items, country)}</SubAccordionTotal>
      </SubAccordionHeader>
      {open && (
        <SectionBlock>
          {items.map((item, index) => (
            <CartItem key={index} item={item} country={country} />
          ))}
        </SectionBlock>
      )}
    </SubAccordion>
  )
}

const CCDesktopMonthlySummary = ({
  getProducts,
  paymentState,
  isAusNZ,
  isAu,
  hasSupportFee,
  supportFee,
  country,
}) => {
  const [monthlyTaxesAndFeesOpen, setMonthlyTaxesAndFeesOpen] = useState(true)
  const [openSections, setOpenSections] = useState({})
  const toggleSection = key =>
    setOpenSections(prev => ({...prev, [key]: prev[key] === false}))

  const hasAnnual = paymentState.annualAddons?.length > 0
  const hasMonthly =
    paymentState.monthlyAddons?.filter(i => i.currency.unitAmount > 0).length >
    0

  const isAnnual = hasAnnual && !hasMonthly
  const monthlyItems = isAnnual
    ? paymentState.annualAddons
    : paymentState.monthlyAddons
  const allItems = getProducts(monthlyItems).filter(
    item => item.code !== '247support-m2m',
  )
  // See cc-desktop-today-summary.js for why this grouping exists — same
  // order_line.location_thryv_id tag, same single-group fallback for
  // single-location/legacy orders.
  const locationGroups = groupItemsByLocation(
    allItems,
    paymentState.paymentInfo?.thryvId,
  )
  const showLocationHeaders = locationGroups.length > 1

  return (
    <>
      <LocationAccordion>
        {locationGroups.map(group => {
          const {software, addons} = categorizeItems(group.items)
          const groupTotal = getGroupTotal(group.items, country)
          const categoryProps = category => ({
            country,
            open: openSections[`${group.index}-${category}`] !== false,
            onToggle: () => toggleSection(`${group.index}-${category}`),
          })
          const categories = (
            <>
              <CategoryAccordion
                label="Software"
                items={software}
                {...categoryProps('software')}
              />
              <CategoryAccordion
                label="Add-ons"
                items={addons}
                {...categoryProps('addons')}
              />
            </>
          )

          if (!showLocationHeaders) return categories

          const locationOpen = openSections[`loc-${group.index}`] !== false
          return (
            <LocationAccordionWrapper key={group.locationThryvId ?? 'none'}>
              <LocationHeader
                onClick={() => toggleSection(`loc-${group.index}`)}
              >
                <LocationMeta>
                  <span className="pin">
                    <Icon name="loc" size={12} />
                  </span>
                  <span className="loc-name">
                    {getLocationLabel(
                      group,
                      paymentState.paymentInfo?.locationNames,
                    )}
                  </span>
                  <span className="loc-count">
                    {' '}
                    - {group.items.length} items
                  </span>
                </LocationMeta>
                <span className="loc-total">
                  {groupTotal}
                  <ChevronIcon open={locationOpen}>
                    <Icon name="downArrow" size="xs" />
                  </ChevronIcon>
                </span>
              </LocationHeader>
              {locationOpen && categories}
            </LocationAccordionWrapper>
          )
        })}
      </LocationAccordion>

      {isAusNZ && hasSupportFee && (
        <SubAccordion style={{marginBottom: '8px'}}>
          <SubAccordionHeader
            onClick={() => setMonthlyTaxesAndFeesOpen(v => !v)}
          >
            <SubAccordionMeta>
              <ChevronIcon open={monthlyTaxesAndFeesOpen}>
                <Icon name="downArrow" />
              </ChevronIcon>
              <div className="sub-category">Fees</div>
            </SubAccordionMeta>
            <SubAccordionTotal />
          </SubAccordionHeader>
          {monthlyTaxesAndFeesOpen && (
            <SectionBlock>
              <div className="d-flex justify-content-between">
                <p className="paragraph-cart-item">
                  24/7 Service &amp; Support Fee
                </p>
                <p className="paragraph-cart-item">
                  {formatPrice(
                    supportFee?.currency?.unitAmount || 0,
                    1,
                    '',
                    country,
                  )}
                </p>
              </div>
            </SectionBlock>
          )}
        </SubAccordion>
      )}
      {isAu && (
        <p
          style={{
            fontSize: 'clamp(11px, 1.2vw, 13px)',
            color: '#4D4D4D',
            marginTop: '10px',
            marginBottom: 0,
          }}
        >
          {'*All plans include GST'}
        </p>
      )}
    </>
  )
}

export {CCDesktopMonthlySummary}
